/**
 * speciesNames — Pet Companion 27 種の表示名マッピング辞書
 *
 * 関連 decision:
 *  - .company/departments/designer/iterations/2026-05-05-archetype-decisions.md
 *  - growth_events 2a4a064a (27 動物 + stage=確信度メタファ)
 *
 * 設計方針:
 *  - 27 動物 = 主軸 OEN (Big Five Top3) × 3 値 (H/M/L) の組み合わせ
 *  - 副軸 CA (誠実性・協調性) は個体差バリエーション (色・装飾・ポーズ) で表現
 *  - 表示名 = 動物名のみ (修飾語廃止)
 *  - DB の `species` カラム (kitsune / lupus / ...) は内部 ID として維持
 *  - PetAvatar.tsx の描画分岐は species 内部 ID を使う
 */

export const speciesDisplayName = {
  ja: {
    // 既存 8 種 (内部 ID 維持)
    kitsune: '狐',          // HMM 想像+平均+平均
    lupus: '狼',            // LHM 現実+社交+平均
    avian: 'スズメ',        // LMM 現実+平均+平均
    lutra: 'カワウソ',      // HHM 想像+社交+平均
    moss: 'ヤマネ',         // LLH 現実+内向+繊細
    lumen: 'うさぎ',        // MHH 平均+社交+繊細
    cervus: '鹿',           // MML 平均+平均+動じない
    baku: '子猫',           // HMH 想像+平均+繊細
    // 新規 19 種
    papilio: 'ハチドリ',    // HHH 想像+社交+繊細 (旧称: 蝶)
    delphinus: 'イルカ',    // HHL 想像+社交+動じない
    psittacus: 'インコ',    // HML 想像+平均+動じない
    strix: 'フクロウ',      // HLH 想像+内向+繊細
    serpens: '蛇',          // HLM 想像+内向+平均
    gekko: 'アホロートル',  // HLL 想像+内向+動じない (旧称: ヤモリ)
    canis: '犬',            // MHM 平均+社交+平均
    equus: '馬',            // MHL 平均+社交+動じない
    erinaceus: 'ハリネズミ', // MMH 平均+平均+繊細
    panda: 'パンダ',        // MMM 完全中庸
    cricetus: 'ハムスター', // MLH 平均+内向+繊細
    testudo: 'カメ',        // MLM 平均+内向+平均
    bradypus: 'ナマケモノ', // MLL 平均+内向+動じない
    ovis: '羊',             // LHH 現実+社交+繊細
    pingu: 'ペンギン',      // LHL 現実+社交+動じない
    cavia: 'モルモット',    // LMH 現実+平均+繊細
    hydrochoerus: 'カピバラ', // LML 現実+平均+動じない
    rana: 'カエル',         // LLM 現実+内向+平均
    crocodylus: 'ワニ',     // LLL 現実+内向+動じない
  },
  en: {
    kitsune: 'Fox',
    lupus: 'Wolf',
    avian: 'Sparrow',
    lutra: 'Otter',
    moss: 'Dormouse',
    lumen: 'Rabbit',
    cervus: 'Deer',
    baku: 'Kitten',
    papilio: 'Hummingbird',
    delphinus: 'Dolphin',
    psittacus: 'Parakeet',
    strix: 'Owl',
    serpens: 'Snake',
    gekko: 'Axolotl',
    canis: 'Dog',
    equus: 'Horse',
    erinaceus: 'Hedgehog',
    panda: 'Panda',
    cricetus: 'Hamster',
    testudo: 'Tortoise',
    bradypus: 'Sloth',
    ovis: 'Lamb',
    pingu: 'Penguin',
    cavia: 'Guinea Pig',
    hydrochoerus: 'Capybara',
    rana: 'Frog',
    crocodylus: 'Crocodile',
  },
} as const

export type Species = keyof typeof speciesDisplayName.ja
export type Locale = keyof typeof speciesDisplayName

/**
 * 種族ごとの一行説明 (日本語)。
 * 「○○ を大事にする人のそばに寄る」という統一形式で、性格の輪郭を端的に伝える。
 * Partner ページ等で「自分はどういう型か」の説明に使う。
 */
export const speciesDescription: Record<Species, string> = {
  // 既存 8 種
  kitsune: '直感・観察・内省を大事にする人のそばに寄る',
  lupus: '統率・集中・連帯を持つ人のそばに寄る',
  avian: '視点の高さ・自由を求める人のそばに寄る',
  lutra: '遊び・水・関係を慈しむ人のそばに寄る',
  moss: '静謐・内向・深さを湛える人のそばに寄る',
  lumen: '灯し・温め・優しさを内に持つ人のそばに寄る',
  cervus: '凛・距離・気配を保つ人のそばに寄る',
  baku: '夢・記憶・包むことを担う人のそばに寄る',
  // 新規 19 種
  papilio: '軽やかな閃きと繊細な観察、尽きない好奇心を持つ人のそばに寄る',
  delphinus: '遊び心と社交性、軽やかな知性を持つ人のそばに寄る',
  psittacus: '好奇心と表現力、人懐こい賢さを持つ人のそばに寄る',
  strix: '静かな観察と深い思索、夜の静寂を愛する人のそばに寄る',
  serpens: '静謐な集中と独特の感性、独立した知性を持つ人のそばに寄る',
  gekko: '飾らないマイペース、独自の世界観を持つ人のそばに寄る',
  canis: '誠実さと温かい関係性、揺るがない信頼を持つ人のそばに寄る',
  equus: '凛とした強さと自由、内に秘めた誇りを持つ人のそばに寄る',
  erinaceus: '慎重さと内省、繊細な優しさを持つ人のそばに寄る',
  panda: 'バランスと穏やかさ、中庸の知恵を持つ人のそばに寄る',
  cricetus: '控えめな繊細さと小さな喜びを大事にする人のそばに寄る',
  testudo: '落ち着いたペースと揺るがない持続力を持つ人のそばに寄る',
  bradypus: '穏やかさと達観、自分のペースを尊ぶ人のそばに寄る',
  ovis: '柔らかな優しさと群れの温かさを大事にする人のそばに寄る',
  pingu: '社交的な明るさと現実的な行動力を持つ人のそばに寄る',
  cavia: '控えめな愛らしさと素朴な温かさを持つ人のそばに寄る',
  hydrochoerus: '鷹揚な穏やかさと懐の深さを持つ人のそばに寄る',
  rana: '物静かさと変化への適応力を持つ人のそばに寄る',
  crocodylus: '静かな威厳と独立した強さを持つ人のそばに寄る',
}

/** OEN (主軸) 値のラベル. 例: H => 'O高 / 想像力豊か' */
export const OEN_AXIS_LABEL: Record<'O'|'C'|'E'|'A'|'N', { name: string; high: string; low: string }> = {
  O: { name: 'Openness (開放性)', high: '想像力豊か・好奇心旺盛', low: '現実的・実直' },
  C: { name: 'Conscientiousness (誠実性)', high: '計画的・整っている', low: '自然体・自由' },
  E: { name: 'Extraversion (外向性)', high: '社交的・活発', low: '内省的・静か' },
  A: { name: 'Agreeableness (協調性)', high: '優しい・共感的', low: '凛々しい・独立的' },
  N: { name: 'Neuroticism (神経症傾向)', high: '繊細・感受性高い', low: '動じない・穏やか' },
}

/**
 * Big Five 主軸 OEN の H/M/L pattern → species 内部 ID マッピング
 * key: "OEN" (e.g. "HMM", "LLH"), value: species 内部 ID
 */
export const oenToSpecies: Record<string, Species> = {
  HHH: 'papilio',
  HHM: 'lutra',
  HHL: 'delphinus',
  HMH: 'baku',
  HMM: 'kitsune',
  HML: 'psittacus',
  HLH: 'strix',
  HLM: 'serpens',
  HLL: 'gekko',
  MHH: 'lumen',
  MHM: 'canis',
  MHL: 'equus',
  MMH: 'erinaceus',
  MMM: 'panda',
  MML: 'cervus',
  MLH: 'cricetus',
  MLM: 'testudo',
  MLL: 'bradypus',
  LHH: 'ovis',
  LHM: 'lupus',
  LHL: 'pingu',
  LMH: 'cavia',
  LMM: 'avian',
  LML: 'hydrochoerus',
  LLH: 'moss',
  LLM: 'rana',
  LLL: 'crocodylus',
}

/**
 * 種族の表示名を取得する。未知の species は内部 ID をそのまま返す。
 */
export function getSpeciesName(species: string, locale: Locale = 'ja'): string {
  const dict = speciesDisplayName[locale] as Record<string, string>
  return dict[species] ?? species
}

/**
 * 個体名 + 種族名 の表示用文字列を返す。
 *  - 初出時 (showSpecies=true): "<個体名> (<種族名>)" / 個体名がなければ "<種族名>"
 *  - 通常 (showSpecies=false): 個体名があれば個体名、なければ種族名
 */
export function formatPetLabel(
  petName: string | null | undefined,
  species: string,
  options: { showSpecies?: boolean; locale?: Locale } = {},
): string {
  const { showSpecies = false, locale = 'ja' } = options
  const speciesName = getSpeciesName(species, locale)
  const trimmed = petName?.trim()
  if (!trimmed) return speciesName
  return showSpecies ? `${trimmed} (${speciesName})` : trimmed
}

/**
 * Big Five 主軸 OEN 値 (H/M/L) から species を決定する。
 * Memory Intelligence の 5 軸推定結果から動物を割り当てる用途。
 */
export function speciesFromOEN(o: 'H'|'M'|'L', e: 'H'|'M'|'L', n: 'H'|'M'|'L'): Species {
  return oenToSpecies[`${o}${e}${n}`]
}
